import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Mail, Key, LogIn, AlertCircle, ArrowLeft, ShieldCheck, CheckCircle2 } from 'lucide-react';

interface AdminLoginProps {
  onBackToSite?: () => void;
}

export default function AdminLogin({ onBackToSite }: AdminLoginProps) {
  const { signInWithEmail, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessInfo(null);
    setIsLoading(true);

    try {
      if (mode === 'login') {
        await signInWithEmail(email.trim(), password);
      } else if (mode === 'forgot') {
        await resetPassword(email.trim());
        setSuccessInfo('Изпратихме линк за възстановяване на паролата на Вашия имейл.');
      }
    } catch (err: any) {
      console.error('Firebase Auth error:', err);
      let msg = 'Възникна грешка при вход.';
      if (
        err.code === 'auth/invalid-credential' || 
        err.code === 'auth/wrong-password' || 
        err.code === 'auth/user-not-found'
      ) {
        msg = 'Грешен имейл адрес или парола. Моля, проверете въведените данни.';
      } else if (err.code === 'auth/weak-password') {
        msg = 'Паролата трябва да бъде поне 6 символа.';
      } else if (err.code === 'auth/too-many-requests') {
        msg = 'Твърде много неуспешни опити. Моля, опитайте отново след малко.';
      } else if (err.code === 'auth/operation-not-allowed') {
        msg = 'Входът с имейл и парола в момента не е разрешен.';
      } else if (err.message) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-[#0F1E32] to-slate-950 flex flex-col justify-center items-center px-4 py-16 font-sans text-slate-800">
      <div className="w-full max-w-md">
        {/* Return Button */}
        {onBackToSite && (
          <button
            onClick={onBackToSite}
            className="inline-flex items-center gap-2 text-slate-300 hover:text-amber-400 text-xs font-semibold uppercase tracking-wider mb-6 transition cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Обратно към публичния сайт
          </button>
        )}

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-700/50">
          {/* Header */}
          <div className="bg-[#0F1E32] p-8 text-center text-white relative border-b border-amber-500/30">
            <div className="w-14 h-14 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center justify-center mx-auto mb-3 text-[#C5A46E]">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-black uppercase tracking-tight text-white">
              Варна Имотен Център
            </h2>
            <p className="text-xs text-amber-300/90 font-medium tracking-wide mt-1">
              Административен панел • Вход с Имейл и Парола
            </p>
          </div>

          <div className="p-8">
            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-start gap-2 animate-fade-in">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" />
                <span className="leading-relaxed font-semibold">{error}</span>
              </div>
            )}

            {successInfo && (
              <div className="mb-5 p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-xs flex items-start gap-2 animate-fade-in">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
                <span className="leading-relaxed">{successInfo}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-amber-600" />
                  Имейл адрес
                </label>
                <input
                  required
                  type="email"
                  placeholder="admin@imoti-varna.bg"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A46E] focus:bg-white transition"
                />
              </div>

              {mode === 'login' ? (
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                      <Key className="w-3.5 h-3.5 text-amber-600" />
                      Парола
                    </label>
                    <button
                      type="button"
                      onClick={() => { setMode('forgot'); setError(null); }}
                      className="text-[11px] text-[#C5A46E] hover:underline font-semibold cursor-pointer"
                    >
                      Забравена парола?
                    </button>
                  </div>
                  <input
                    required
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A46E] focus:bg-white transition"
                  />
                </div>
              ) : (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(null); }}
                    className="text-xs text-[#C5A46E] hover:underline font-semibold cursor-pointer"
                  >
                    ← Обратно към вход с парола
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 px-4 bg-[#0F1E32] hover:bg-amber-600 text-white font-bold text-xs uppercase tracking-widest rounded-xl transition shadow-md hover:shadow-lg disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2 mt-3"
              >
                {isLoading ? (
                  <span>Обработка...</span>
                ) : mode === 'login' ? (
                  <>
                    <LogIn className="w-4 h-4" />
                    <span>Вход в панела</span>
                  </>
                ) : (
                  <span>Изпрати линк за нова парола</span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
